"use client";

import Image from "next/image";
import { HTMLAttributes, ReactNode } from "react";

type PolaroidSize = "sm" | "md" | "lg";

interface PolaroidCardProps extends HTMLAttributes<HTMLDivElement> {
  imageSrc?: string;
  imageAlt?: string;
  caption?: string;
  size?: PolaroidSize;
  rotate?: string;
  children?: ReactNode;
}

const sizeStyles: Record<
  PolaroidSize,
  { container: string; image: string; caption: string }
> = {
  sm: { container: "w-[200px]", image: "h-[140px]", caption: "h-[60px]" },
  md: { container: "w-[260px]", image: "h-[180px]", caption: "h-[80px]" },
  lg: { container: "w-[320px]", image: "h-[240px]", caption: "h-[100px]" },
};

export function PolaroidCard({
  imageSrc,
  imageAlt = "",
  caption,
  size = "md",
  rotate = "-2deg",
  className = "",
  children,
  style,
  ...props
}: PolaroidCardProps) {
  const sizeStyle = sizeStyles[size];

  return (
    <div
      className={`relative bg-white p-3 pb-0 rounded-sm shadow-md transition-all duration-300 hover:rotate-0 hover:-translate-y-2 hover:shadow-xl ${sizeStyle.container} ${className}`}
      style={{
        transform: `rotate(${rotate})`,
        ...style,
      }}
      {...props}
    >
      {/* Image Area */}
      <div
        className={`relative bg-neutral-100 rounded-sm overflow-hidden ${sizeStyle.image}`}
      >
        {imageSrc ? (
          <Image
            fill
            src={imageSrc}
            alt={imageAlt}
            className="object-cover"
            sizes="320px"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-400">
            <svg
              className="w-12 h-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Caption Area */}
      <div
        className={`flex items-center justify-center p-2 ${sizeStyle.caption}`}
      >
        {caption ? (
          <p className="text-sm text-neutral-700 text-center line-clamp-2 font-medium">
            {caption}
          </p>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

export type { PolaroidCardProps, PolaroidSize };
